import React, { type JSX } from 'react';
import { Box, ToggleButtonGroup } from '@mui/material';
import { ErrorMessageText } from '../../../input/common';
import { ToggleButton } from './ToggleButton';

type ToggleValue = { value: string | number };
type LegacyMultiSelectValue = { values: ToggleValue[]; comment?: string };
type LegacyMultiSelectOption = {
  label: string;
  value: ToggleValue['value'];
  commentEligibility?: string;
};

type LegacyMultiSelectProps = {
  options: LegacyMultiSelectOption[];
  value: LegacyMultiSelectValue;
  onChange: (value: LegacyMultiSelectValue) => void;
  error?: string;
  disabled?: boolean;
};

export const LegacyMultiSelect = ({
  options,
  value,
  onChange,
  error,
  disabled,
}: LegacyMultiSelectProps) => {
  const optionValues = options.map((option) => option.value);

  const isNaOption = (optionValue: string | number) => optionValue === 'na';

  const handleToggleButtonChange = (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    nextValue: ToggleValue
  ) => {
    const optionIdx = optionValues.indexOf(nextValue.value);

    const currentValues = value.values;
    const isSelected = value.values
      .map((currentValue) => currentValue.value)
      .includes(nextValue.value);

    // pad nextValues with placeholders
    const nextValues: ToggleValue[] = optionValues.map(() => ({
      value: '',
    }));

    const clickedIsNa = isNaOption(nextValue.value);

    if (clickedIsNa && !isSelected) {
      // Selecting N/A: clear all other selections — N/A is exclusive
      nextValues[optionIdx] = { value: nextValue.value };
    } else {
      // add in current values, but strip N/A when selecting a non-N/A option
      currentValues.forEach((currentValue) => {
        const currentValueIdx = optionValues.indexOf(currentValue.value);
        if (currentValueIdx === -1) return;
        if (!isSelected && !clickedIsNa && isNaOption(currentValue.value))
          return;
        nextValues[currentValueIdx] = currentValue;
      });

      nextValues[optionIdx] = isSelected
        ? { value: '' }
        : { value: nextValue.value };
    }

    onChange({ values: nextValues });
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        <ToggleButtonGroup
          sx={{ flexWrap: 'nowrap' }}
          value={value.values}
          disabled={disabled}
        >
          {options.map((option) => {
            const isOptionSelected = value.values.some((val) => {
              return val.value == option.value;
            });

            const optionValue = { value: option.value };

            return (
              <ToggleButton
                key={option.value}
                value={optionValue}
                selected={isOptionSelected}
                onClick={handleToggleButtonChange}
              >
                {option.label}
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
      </Box>

      <ErrorMessageText>{error}</ErrorMessageText>
    </>
  );
};

type LegacyMultiSelectContainerProps = {
  labelEl: JSX.Element | null;
} & LegacyMultiSelectProps;

export const LegacyMultiSelectContainer = (
  props: LegacyMultiSelectContainerProps
) => {
  return (
    <React.Fragment>
      {props.labelEl}

      <LegacyMultiSelect {...props} />
    </React.Fragment>
  );
};
